import { useState, useEffect, useCallback } from 'react';
import { smsCampaignApi } from '../../api/smsCampaign.api';
import { companyApi } from '../../api/company.api';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { useDebounce } from '../../hooks/useDraft';
import {
  PageHeader, Btn, Input, Textarea, Select, Modal, ConfirmDialog, Badge, EmptyState, Skeleton,
} from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';

const TARGET_OPTIONS = [
  { value: 'users', label: 'کاربران (مشتریان شرکت‌ها)' },
  { value: 'employees', label: 'کارمندان' },
  { value: 'both', label: 'کاربران و کارمندان' },
];
const TARGET_LABEL = { users: 'کاربران', employees: 'کارمندان', both: 'کاربران و کارمندان' };
const STATUS = {
  draft: { label: 'پیش‌نویس', variant: 'gray' },
  sending: { label: 'در حال ارسال', variant: 'amber' },
  sent: { label: 'ارسال‌شده', variant: 'green' },
};
const PLACEHOLDERS = [
  ['{first_name}', 'نام'], ['{last_name}', 'نام خانوادگی'], ['{full_name}', 'نام کامل'],
  ['{username}', 'نام کاربری'], ['{company}', 'شرکت (کاربران)'], ['{department}', 'دپارتمان (کارمندان)'],
];
const EMPTY = {
  id: null, title: '', text: '', target: 'users', send_to_all: false,
  company_ids: [], department_ids: [], user_ids: [], employee_ids: [],
};

const box = { border: '1.5px solid #e2e8f0', borderRadius: 8, padding: 10, maxHeight: 180, overflow: 'auto', background: '#fff' };
const sectionTitle = { fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 };

function CheckList({ items, selected, onChange, empty }) {
  const [q, setQ] = useState('');
  const shown = items.filter((i) => !q || i.name.includes(q));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div>
      <Input placeholder="جستجو..." value={q} onChange={(e) => setQ(e.target.value)} wrapStyle={{ marginBottom: 6 }} />
      <div style={box}>
        {shown.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>{empty}</span>}
        {shown.map((i) => (
          <label key={i.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={selected.includes(i.id)} onChange={() => toggle(i.id)} />
            {i.name}
          </label>
        ))}
      </div>
    </div>
  );
}

function PersonPicker({ kind, label, picked, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const dq = useDebounce(q, 300);
  useEffect(() => {
    if (dq.trim().length < 2) { setResults([]); return; }
    smsCampaignApi.searchRecipients(kind, dq.trim())
      .then((r) => setResults(r.data?.results || []))
      .catch(() => setResults([]));
  }, [dq, kind]);
  const add = (p) => {
    if (!picked.some((x) => x.id === p.id)) onChange([...picked, p]);
    setQ(''); setResults([]);
  };
  return (
    <div>
      <div style={sectionTitle}>{label}</div>
      <Input placeholder="نام، نام کاربری یا شماره (حداقل ۲ حرف)" value={q} onChange={(e) => setQ(e.target.value)} />
      {results.length > 0 && (
        <div style={{ ...box, marginTop: 6 }}>
          {results.map((p) => (
            <div key={p.id} onClick={() => add(p)} style={{ padding: '6px 4px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' }}>
              {p.name} <span dir="ltr" style={{ color: '#94a3b8' }}>({p.username}{p.phone ? ` · ${p.phone}` : ''})</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
        {picked.map((p) => (
          <span key={p.id} style={{ background: '#EDF3FC', color: '#233A7A', borderRadius: 999, padding: '4px 10px', fontSize: 12, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {p.name}
            <button type="button" aria-label="حذف" onClick={() => onChange(picked.filter((x) => x.id !== p.id))}
              style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 0 }}>✕</button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function SmsCampaignPage() {
  const { addToast } = useUI();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [form, setForm] = useState(null);          // null = editor closed
  const [pickedUsers, setPickedUsers] = useState([]);
  const [pickedEmployees, setPickedEmployees] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [confirmSend, setConfirmSend] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sending, setSending] = useState(false);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = useCallback(() => smsCampaignApi.list()
    .then((r) => setCampaigns(r.data?.campaigns || []))
    .catch(() => addToast('خطا در بارگذاری کمپین‌ها', 'error')), [addToast]);

  useEffect(() => {
    load().finally(() => setLoading(false));
    companyApi.getCompanyListView()
      .then((r) => setCompanies((Array.isArray(r.data) ? r.data : []).map((c) => ({ id: c.id, name: c.name || '' }))))
      .catch(() => {});
    departmentApi.getDepartmentListView()
      .then((r) => setDepartments((Array.isArray(r.data) ? r.data : []).map((d) => ({ id: d.id, name: d.name || '' }))))
      .catch(() => {});
  }, [load]);

  // poll while any campaign is being sent so counters update
  useEffect(() => {
    if (!campaigns.some((c) => c.status === 'sending')) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [campaigns, load]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setPreview(null); };

  const openNew = () => { setForm({ ...EMPTY }); setPickedUsers([]); setPickedEmployees([]); setPreview(null); };

  const openEdit = async (c) => {
    try {
      const r = await smsCampaignApi.get(c.id);
      setForm({ ...EMPTY, ...r.data });
      setPickedUsers(r.data.users || []);
      setPickedEmployees(r.data.employees || []);
      setPreview(null);
    } catch {
      addToast('خطا در بارگذاری کمپین', 'error');
    }
  };

  const payload = () => ({
    ...form,
    user_ids: pickedUsers.map((p) => p.id),
    employee_ids: pickedEmployees.map((p) => p.id),
  });

  const insertPlaceholder = (token) => set({ text: `${form.text}${token}` });

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const r = await smsCampaignApi.preview(payload());
      setPreview(r.data);
    } catch (e) {
      addToast(typeof e.response?.data === 'string' ? e.response.data : 'خطا در محاسبه مخاطبان', 'error');
    }
    setPreviewing(false);
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await smsCampaignApi.save(payload());
      addToast('کمپین ذخیره شد', 'success');
      setForm(null);
      load();
    } catch (e) {
      addToast(typeof e.response?.data === 'string' ? e.response.data : 'خطا در ذخیره', 'error');
    }
    setSaving(false);
  };

  const doSend = async () => {
    setSending(true);
    try {
      await smsCampaignApi.send(confirmSend.id);
      addToast('ارسال پیامک‌ها آغاز شد', 'success');
      setConfirmSend(null);
      load();
    } catch (e) {
      addToast(typeof e.response?.data === 'string' ? e.response.data : 'خطا در ارسال', 'error');
    }
    setSending(false);
  };

  const doDelete = async () => {
    try {
      await smsCampaignApi.remove(confirmDelete.id);
      addToast('کمپین حذف شد', 'success');
      setConfirmDelete(null);
      load();
    } catch (e) {
      addToast(typeof e.response?.data === 'string' ? e.response.data : 'خطا در حذف', 'error');
    }
  };

  const openLogs = async (c) => {
    setLogsFor(c); setLogs([]);
    try {
      const r = await smsCampaignApi.logs(c.id);
      setLogs(r.data?.logs || []);
    } catch {
      addToast('خطا در دریافت گزارش', 'error');
    }
  };

  const showCompanies = form && (form.target === 'users' || form.target === 'both');
  const showDepartments = form && (form.target === 'employees' || form.target === 'both');
  const len = form ? form.text.length : 0;

  return (
    <div className="fade-in">
      <PageHeader
        title="کمپین پیامکی"
        subtitle="تعریف قالب پیامک و ارسال به کاربران یا کارمندان (شرکت، دپارتمان یا فرد مشخص)"
        action={<Btn onClick={openNew}>+ کمپین جدید</Btn>}
      />

      {loading ? <Skeleton h={90} /> : campaigns.length === 0 ? (
        <EmptyState title="هنوز کمپینی تعریف نشده" subtitle="با «کمپین جدید» اولین قالب پیامک را بسازید" />
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {campaigns.map((c) => {
            const st = STATUS[c.status] || STATUS.draft;
            return (
              <div key={c.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(35,58,122,0.1)', padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 15 }}>{c.title}</strong>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <Badge variant="blue">{TARGET_LABEL[c.target]}</Badge>
                    <Badge variant={st.variant}>{st.label}</Badge>
                  </span>
                </div>
                <p style={{ fontSize: 13, color: '#475569', margin: '8px 0', whiteSpace: 'pre-wrap' }}>{c.text}</p>
                {c.last_sent_at && (
                  <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>
                    آخرین ارسال: {c.last_sent_at} — موفق {toPersian(c.last_success)} از {toPersian(c.last_total)}
                    {c.last_failed > 0 ? ` (ناموفق ${toPersian(c.last_failed)})` : ''}
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Btn size="sm" disabled={c.status === 'sending'} onClick={() => setConfirmSend(c)}>ارسال</Btn>
                  <Btn size="sm" variant="secondary" disabled={c.status === 'sending'} onClick={() => openEdit(c)}>ویرایش</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => openLogs(c)}>گزارش ارسال</Btn>
                  <Btn size="sm" variant="danger" disabled={c.status === 'sending'} onClick={() => setConfirmDelete(c)}>حذف</Btn>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? 'ویرایش کمپین' : 'کمپین جدید'} width={680}>
        {form && (
          <div style={{ display: 'grid', gap: 16 }}>
            <Input label="عنوان کمپین" value={form.title} maxLength={150} onChange={(e) => set({ title: e.target.value })} />
            <div>
              <Textarea label="متن پیامک" emoji={false} value={form.text} maxLength={700}
                onChange={(e) => set({ text: e.target.value })} />
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {PLACEHOLDERS.map(([token, name]) => (
                    <button key={token} type="button" onClick={() => insertPlaceholder(token)}
                      style={{ border: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: 6, padding: '2px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {name}
                    </button>
                  ))}
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>{toPersian(len)} / {toPersian(700)}</span>
              </div>
            </div>

            <Select label="ارسال برای" value={form.target} options={TARGET_OPTIONS} onChange={(e) => set({ target: e.target.value })} />

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.send_to_all} onChange={(e) => set({ send_to_all: e.target.checked })} />
              همه {TARGET_LABEL[form.target]} (بدون فیلتر شرکت/دپارتمان)
            </label>

            {!form.send_to_all && (
              <>
                {showCompanies && (
                  <div>
                    <div style={sectionTitle}>شرکت‌ها (همه کاربران شرکت‌های انتخاب‌شده)</div>
                    <CheckList items={companies} selected={form.company_ids} empty="شرکتی یافت نشد"
                      onChange={(ids) => set({ company_ids: ids })} />
                  </div>
                )}
                {showDepartments && (
                  <div>
                    <div style={sectionTitle}>دپارتمان‌ها (همه کارمندان دپارتمان‌های انتخاب‌شده)</div>
                    <CheckList items={departments} selected={form.department_ids} empty="دپارتمانی یافت نشد"
                      onChange={(ids) => set({ department_ids: ids })} />
                  </div>
                )}
                {showCompanies && (
                  <PersonPicker kind="user" label="کاربران مشخص" picked={pickedUsers}
                    onChange={(p) => { setPickedUsers(p); setPreview(null); }} />
                )}
                {showDepartments && (
                  <PersonPicker kind="employee" label="کارمندان مشخص" picked={pickedEmployees}
                    onChange={(p) => { setPickedEmployees(p); setPreview(null); }} />
                )}
              </>
            )}

            {preview && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 13 }}>
                <div><strong>{toPersian(preview.count)}</strong> مخاطب دارای شماره معتبر
                  {preview.skipped_no_phone > 0 && ` (${toPersian(preview.skipped_no_phone)} نفر بدون شماره معتبر حذف شدند)`}
                </div>
                {preview.count > 0 && (
                  <>
                    <div style={{ marginTop: 8, color: '#64748b' }}>نمونه متن برای نفر اول:</div>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{preview.rendered_text}</div>
                    <div style={{ marginTop: 8, color: '#64748b' }}>
                      {preview.sample.map((s) => s.name).join('، ')}{preview.count > preview.sample.length ? ' ...' : ''}
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" loading={previewing} onClick={doPreview}>پیش‌نمایش مخاطبان</Btn>
              <Btn loading={saving} onClick={doSave}>ذخیره</Btn>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmSend}
        onClose={() => setConfirmSend(null)}
        onConfirm={doSend}
        title="ارسال کمپین"
        message={confirmSend ? `کمپین «${confirmSend.title}» برای همه مخاطبان تعریف‌شده ارسال شود؟` : ''}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="حذف کمپین"
        message={confirmDelete ? `کمپین «${confirmDelete.title}» و گزارش‌های آن حذف شود؟` : ''}
        danger
      />

      <Modal open={!!logsFor} onClose={() => setLogsFor(null)} title={logsFor ? `گزارش ارسال: ${logsFor.title}` : ''} width={680}>
        {logs.length === 0 ? <EmptyState title="هنوز گزارشی ثبت نشده" /> : (
          <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'right', color: '#64748b' }}>
                  <th style={{ padding: 6 }}>گیرنده</th><th>شماره</th><th>زمان</th><th>وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 6 }}>{l.name} <span style={{ color: '#94a3b8' }}>({l.type === 'employee' ? 'کارمند' : 'کاربر'})</span></td>
                    <td dir="ltr">{l.phone}</td>
                    <td dir="ltr">{l.sent_at}</td>
                    <td><Badge variant={l.success ? 'green' : 'red'}>{l.success ? 'ارسال شد' : 'ناموفق'}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
